import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import { Phone, MapPin, CheckCircle2, ArrowRight, Paintbrush, ExternalLink } from 'lucide-react';
import { companyInfo } from '../data/mock';

const PaintingDecorating = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', postcode: '', message: '', honeypot: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return;
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name, email: formData.email, phone: formData.phone,
          properties_managed: formData.postcode,
          job_description: `[Painting & Decorating Enquiry] ${formData.message}`,
          trade_type: 'decorating',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: '/painting-decorating'
        }),
      });
      if (response.ok) { setIsSubmitted(true); setFormData({ name: '', email: '', phone: '', postcode: '', message: '', honeypot: '' }); }
      else { setSubmitError(true); }
    } catch { setSubmitError(true); }
    finally { setIsSubmitting(false); }
  };

  const services = [
    { title: 'Rental Property Decorating', desc: 'Full property repaints and refreshes to prepare properties for new tenants' },
    { title: 'Single Room Decorating', desc: 'Individual room painting — bedrooms, living rooms, kitchens, hallways' },
    { title: 'Walls & Ceilings', desc: 'Emulsion painting to walls and ceilings throughout properties' },
    { title: 'Woodwork', desc: 'Gloss and satinwood painting to doors, skirting, architrave and window frames' },
    { title: 'Property Refreshes', desc: 'Quick turnaround decorating to freshen up tired-looking properties' },
    { title: 'End of Tenancy Decorating', desc: 'Preparing properties between tenancies to minimise void periods' },
    { title: 'Refurbishment Decorating', desc: 'Decorating as part of larger refurbishment and renovation projects' },
    { title: 'Preparation & Repairs', desc: 'Filling, sanding, preparation — proper groundwork before painting' }
  ];

  const benefits = [
    'Experienced, professional decorators',
    'Clean, tidy working — we protect your property',
    'Quality paints and materials',
    'Proper preparation for lasting results',
    '£5 million public liability insurance',
    '12-month workmanship guarantee'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Painting & Decorating Manchester | Rental Property Decorating | Expert Property Maintenance</title>
        <meta name="description" content="Painting and decorating in Manchester for landlords and homeowners. Rental property decorating, end of tenancy refreshes, walls, ceilings, woodwork. Call 07300 405045." />
        <link rel="canonical" href="https://expert-propertymaintenance.co.uk/painting-decorating" />
      </Helmet>

      <Header variant="default" companyPhone={companyInfo.phone} />

      {/* Hero */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-20 bg-gradient-to-b from-brand-navy to-brand-navy/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Paintbrush className="w-4 h-4" />
              Painting & Decorating
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Painting & Decorating Manchester
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Professional painting and decorating for landlords, letting agents and homeowners across 
              Greater Manchester. From single rooms to full property repaints.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                <a href="#contact">Request a Quote<ArrowRight className="w-5 h-5 ml-2" /></a>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy" asChild>
                <a href={`tel:${companyInfo.phone}`}><Phone className="w-5 h-5 mr-2" />{companyInfo.phone}</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-brand-navy mb-6">Professional Decorating Services</h2>
              <p className="text-lg text-brand-slate mb-6">
                Fresh decorating transforms a property. For landlords, it makes the difference between a 
                property that lets quickly and one that sits empty. For homeowners, it brings new life 
                to tired rooms.
              </p>
              <p className="text-brand-slate mb-6">
                Expert Property Maintenance provides painting and decorating services across Greater Manchester. 
                We work efficiently to minimise disruption, prepare surfaces properly for lasting results, and 
                leave your property clean and ready to use.
              </p>
              <p className="text-brand-slate mb-8">
                Whether you need a single room painted, a full property refresh between tenancies, or decorating 
                as part of a larger refurbishment, we deliver quality results.
              </p>
              <div className="space-y-3">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                    <span className="text-brand-slate">{benefit}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 p-4 bg-brand-grey border border-slate-200 rounded-xl">
                <p className="text-sm text-brand-slate mb-2">Our specialist decorating brand:</p>
                <a href="https://expert-painter.co.uk" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-brand-yellow hover:text-yellow-300 font-semibold">
                  Expert Painter <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </div>
            </div>
            <div className="bg-brand-grey rounded-2xl p-8">
              <h3 className="text-xl font-bold text-brand-navy mb-6">Decorating Services</h3>
              <div className="space-y-4">
                {services.map((service) => (
                  <div key={service.title} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium text-brand-navy">{service.title}</span>
                      <p className="text-sm text-brand-slate">{service.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Landlords */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-brand-navy rounded-2xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-white mb-4">Decorating for Rental Properties</h2>
                <p className="text-slate-300 mb-6">
                  Rental properties need regular repainting — scuffs, marks and general wear build up over time. 
                  Fresh decorating makes a significant difference to letting speed and can justify higher rents.
                </p>
                <p className="text-slate-400 text-sm mb-6">
                  We understand landlords need fast turnarounds. We work efficiently between tenancies to minimise 
                  void periods while still delivering quality results.
                </p>
                <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                  <a href="/landlord-services">All Landlord Services<ArrowRight className="w-5 h-5 ml-2" /></a>
                </Button>
              </div>
              <div className="space-y-4">
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">End of Tenancy</h4>
                  <p className="text-sm text-slate-400">Full or partial decorating between tenancies</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">Void Turnaround</h4>
                  <p className="text-sm text-slate-400">Quick refresh to get properties let faster</p>
                </div>
                <div className="bg-slate-800 rounded-xl p-5">
                  <h4 className="font-semibold text-white mb-1">Portfolio Decorating</h4>
                  <p className="text-sm text-slate-400">Scheduled repainting across multiple properties</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-brand-navy mb-8">Related Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <a href="/plastering-repairs" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Plastering & Repairs</h4>
              <p className="text-sm text-brand-slate">Preparation before decorating</p>
            </a>
            <a href="/end-of-tenancy" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">End of Tenancy</h4>
              <p className="text-sm text-brand-slate">Repairs between tenancies</p>
            </a>
            <a href="/void-property-works" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Void Property Works</h4>
              <p className="text-sm text-brand-slate">Complete turnarounds</p>
            </a>
            <a href="/property-refurbishment" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Property Refurbishment</h4>
              <p className="text-sm text-brand-slate">Full renovations</p>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-16 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-6">Get a Decorating Quote</h2>
              <p className="text-slate-300 text-lg mb-8">Tell us about your decorating requirements and we will provide a quote.</p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center"><Phone className="w-6 h-6 text-brand-navy" /></div>
                  <div>
                    <div className="text-slate-400 text-sm">Call us</div>
                    <a href={`tel:${companyInfo.phone}`} className="text-white font-bold text-lg hover:text-brand-yellow">{companyInfo.phone}</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center"><MapPin className="w-6 h-6 text-brand-navy" /></div>
                  <div>
                    <div className="text-slate-400 text-sm">Covering</div>
                    <div className="text-white font-bold">Greater Manchester & Cheshire</div>
                  </div>
                </div>
              </div>
            </div>
            <Card className="bg-white border-0">
              <CardContent className="p-6 sm:p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8 text-green-600" /></div>
                    <h3 className="text-xl font-bold text-brand-navy mb-2">Enquiry Received</h3>
                    <p className="text-brand-slate">We will be in touch with a quote.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="text-xl font-bold text-brand-navy mb-4">Request a Quote</h3>
                    <input type="text" name="website" value={formData.honeypot} onChange={(e) => setFormData({...formData, honeypot: e.target.value})} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                    <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label><Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" className="h-12" /></div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label><Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@email.com" className="h-12" /></div>
                      <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Phone *</label><Input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="07xxx xxxxxx" className="h-12" /></div>
                    </div>
                    <div><label className="block text-sm font-medium text-brand-slate mb-1.5">Postcode</label><Input value={formData.postcode} onChange={(e) => setFormData({...formData, postcode: e.target.value})} placeholder="e.g., M1 1AA" className="h-12" /></div>
                    <div><label className="block text-sm font-medium text-brand-slate mb-1.5">What needs decorating? *</label><Textarea required value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="Number of rooms, type of work required..." rows={4} /></div>
                    <Button type="submit" className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 text-lg" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Send Enquiry'}</Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© {new Date().getFullYear()} Expert Property Maintenance</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/" className="hover:text-brand-yellow">Home</a>
              <a href="/landlord-services" className="hover:text-brand-yellow">Landlord Services</a>
              <a href="/#contact" className="hover:text-brand-yellow">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      <MobileActionBar phone={companyInfo.phone} />
    </div>
  );
};

export default PaintingDecorating;
